import React, { useMemo, useState } from 'react';
import { OrgBadge, teamName } from '../../components/PlacementsTable';
import { TEAM_LABELS } from '../../config';
import { fmtDate, fmtMoney, fmtNumber, isoDate, useFetch, usePagedSimple } from '../../hooks';
import { Purpose, Alert, Badge, Card, Chip, Progress, Segmented, Select, Skeleton, Stat } from '../../ui/basics';
import { LineChart } from '../../ui/charts';
import { DataTable } from '../../ui/DataTable';
import { Drawer } from '../../ui/overlay';

const THRESHOLDS = [7, 14, 30, 60, 90];
const WINDOWS = [{ value: 6, label: 'Started in the last 6 months' }, { value: 12, label: 'Started in the last 12 months' }, { value: 24, label: 'Started in the last 24 months' }];
const monthsAgo = (n) => { const d = new Date(); d.setMonth(d.getMonth() - n); return isoDate(d); };
const label = (groupBy, g) => (groupBy === 'lead' ? TEAM_LABELS[g] || g : g);

function Drill({ params, group, groupBy, combined, threshold, onClose }) {
  const [outcome, setOutcome] = useState('early');
  const list = usePagedSimple('analytics/attrition/placements/', { ...params, group, outcome }, 10);
  const columns = [
    { key: 'candidate_name', label: 'Candidate', render: (r) => <b>{r.candidate_name}</b> },
    ...(combined ? [{ key: 'organization', label: 'Organization', render: (r) => <OrgBadge name={r.organization} /> }] : []),
    { key: 'client', label: 'Client' }, { key: 'recruiter', label: 'Recruiter' },
    { key: 'lead', label: 'Team', render: (r) => teamName(r.lead) },
    { key: 'date_of_joining', label: 'Started', render: (r) => fmtDate(r.date_of_joining) },
    { key: 'project_end', label: 'Ended', render: (r) => fmtDate(r.project_end) },
    { key: 'days', label: 'Lasted', align: 'right', render: (r) => (r.days === null ? <Badge tone="success">still active</Badge> : <Badge tone={r.early ? 'danger' : ''}>{r.days} days</Badge>) },
    { key: 'gross_margin', label: 'Margin / hr', align: 'right', render: (r) => fmtMoney(r.gross_margin) },
  ];
  return (
    <Drawer wide title={`${label(groupBy, group)} · placements`} onClose={onClose}>
      <div className="stack">
        <Segmented value={outcome} onChange={setOutcome} options={[{ value: 'early', label: `Ended within ${threshold} days` }, { value: 'all', label: 'All placements judged' }]} />
        <div className="card flush"><DataTable columns={columns} paged={list} rowKey={(r) => r.s_no} noun="placements" /></div>
      </div>
    </Drawer>
  );
}

export default function Attrition({ org, lead }) {
  const [threshold, setThreshold] = useState(30);
  const [windowMonths, setWindowMonths] = useState(12);
  const [groupBy, setGroupBy] = useState('client');
  const [sort, setSort] = useState({ key: 'rate', dir: -1 });
  const [drill, setDrill] = useState(null);
  const params = useMemo(() => ({ organization: org, lead, threshold, from_date: monthsAgo(windowMonths), to_date: isoDate(new Date()), group_by: groupBy, min_n: 10 }),
    [org, lead, threshold, windowMonths, groupBy]);
  const { data, loading, error } = useFetch('analytics/attrition/', params);
  const o = data && data.overall;
  const groups = useMemo(() => (data ? [...data.groups].sort((a, b) => (a[sort.key] > b[sort.key] ? 1 : a[sort.key] < b[sort.key] ? -1 : 0) * sort.dir) : []), [data, sort]);
  const groupOptions = [['client', 'Client'], ['recruiter', 'Recruiter'], ['lead', 'Team'], ['source', 'Source'], ...(org === 'combined' ? [['organization', 'Organization']] : [])];
  const head = (key, text, right) => (
    <th className={`sortable ${right ? 'r' : ''}`} onClick={() => setSort((s) => ({ key, dir: s.key === key ? -s.dir : -1 }))}>
      {text}{sort.key === key && <span className="arrow">{sort.dir === -1 ? '↓' : '↑'}</span>}</th>);

  return (
    <div className="stack">
      <Purpose what="Shows how many placements end soon after they start, and where that happens most, so the team can find the clients, recruiters, sources or teams that lose people early." use="Choose the cut-off (for example 30 days) and the period, then group by client, recruiter, team, source or organization. Click a row to see the actual placements." care="Only placements old enough to judge are counted, and active ones count as having stayed. Some clients hire for short assignments, so a high rate points to where to look, not proof of a problem." />
      <Card title="Early attrition" subtitle="How many placements end soon after they start, and where it concentrates."
        actions={<div className="row">
          <div className="row" style={{ gap: 6 }}><span className="muted">Ended within</span>{THRESHOLDS.map((t) => <Chip key={t} on={t === threshold} onClick={() => setThreshold(t)}>{t} days</Chip>)}</div>
          <Select value={windowMonths} onChange={(e) => setWindowMonths(Number(e.target.value))} options={WINDOWS} aria-label="Window" />
        </div>}>
        {error && <Alert>{error}</Alert>}
        {!o ? <Skeleton h={70} /> : (
          <div className="stack">
            <div className="grid cols-4">
              <Stat label="Placements judged" value={fmtNumber(o.n)} sub={`started ${fmtDate(data.from_date)} – ${fmtDate(data.to_date)}`} />
              <Stat label={`Ended within ${threshold} days`} value={fmtNumber(o.early)} sub={`${o.rate}% of placements judged`} />
              <Stat label="Typical length of ended placements" value={o.median_days === null ? '—' : `${o.median_days} days`} sub="median" />
              <Stat label="Still active" value={fmtNumber(o.active)} sub="count as staying" />
            </div>
            <p className="muted">Only placements that started at least {threshold} days ago are judged, so recent starts are not penalised for not having had time to end.
              {data.set_aside > 0 && ` ${data.set_aside} placements with unusable dates were set aside (see Data health).`}</p>
          </div>
        )}
      </Card>

      <Card title="By start month" subtitle={`Share of placements ending within ${threshold} days (months with fewer than 5 placements are not drawn)`}>
        {loading && !data ? <Skeleton h={260} /> : data && <LineChart height={260} yFormat={(v) => `${v}%`}
          data={[{ id: `Ended within ${threshold} days`, data: data.trend.map((t) => ({ x: t.month, y: t.n >= 5 ? t.rate : null })) }]} />}
      </Card>

      <Card flush title="Where it concentrates" subtitle={`Click a row to see the placements. Groups with fewer than 10 placements are hidden${data && data.hidden_groups ? ` (${data.hidden_groups} hidden)` : ''}.`}
        actions={<Segmented value={groupBy} onChange={(g) => { setGroupBy(g); setSort({ key: 'rate', dir: -1 }); }} options={groupOptions.map(([value, text]) => ({ value, label: text }))} />}>
        <div className="table-wrap"><table className="table">
          <thead><tr>{head('group', groupOptions.find(([v]) => v === groupBy)[1])}{head('n', 'Placements', true)}{head('early', 'Ended early', true)}
            <th style={{ width: 200 }}>{`Ended within ${threshold} days`}</th>{head('rate', 'Rate', true)}{head('delta', 'vs overall', true)}{head('median_days', 'Median days', true)}{head('active', 'Still active', true)}</tr></thead>
          <tbody>
            {groups.map((g) => (
              <tr key={g.group} className="clickable" onClick={() => setDrill(g.group)}>
                <td><b>{label(groupBy, g.group)}</b></td><td className="r num">{g.n}</td><td className="r num">{g.early}</td>
                <td><Progress value={g.rate} max={100} /></td><td className="r num">{g.rate}%</td>
                <td className="r num"><span style={{ color: g.delta > 0 ? 'var(--danger)' : g.delta < 0 ? '#027a48' : undefined }}>{g.delta > 0 ? '+' : ''}{g.delta} pts</span></td>
                <td className="r num">{g.median_days === null ? '—' : g.median_days}</td><td className="r num">{g.active}</td>
              </tr>
            ))}
          </tbody>
        </table>
          {data && groups.length === 0 && <div className="empty"><strong>No groups to show</strong>Try a longer window or a different grouping.</div>}</div>
        <div className="pager"><span>Groups differ in the kind of work (some clients hire for short assignments), so treat this as a pointer to where to look, not proof of a cause.</span></div>
      </Card>
      {drill !== null && <Drill params={params} group={drill} groupBy={groupBy} combined={org === 'combined'} threshold={threshold} onClose={() => setDrill(null)} />}
    </div>
  );
}
